import { defineStore } from 'pinia'
import crypto from 'crypto-js'

// NOTE: 永続化による意図せぬトラブルを避けるため state に入れない
const isRefreshingTokens = false

export const useStore = defineStore('index', {

  state: () => ({
    accessToken: '',
    refreshToken: '',
    isLoggedIn: false,
    isRefreshingTokens: false,
    chainKey: useRuntimeConfig().public.chainKey,
    chainId: 0,
    mediaId: useRuntimeConfig().public.mediaId,
    chain: {
      chainId: 0,
      chainKey: '',
      chainName: '',
      chainLogo: '',
      serviceName: '',
      serviceFullName: '',
      serviceLogo: '',
      pointName: '',
      emoneyName: '',
      cardName: '',
      card: {
        isRegist: 0,
        displayCardNo: '',
        cardNoSample: '',
        authKeys: [],
        cardNote: '',
      },
      dispBalance: {
        point: 0,
        emony: 0,
        usageAmount: 0,
      },
      callUrl: '',
    },
    media: {
      mediaId: 0,
      menuType: '',
      menu: [],
      options: {},
    },
    user: {
      appNo: '',
      impressionKey: '',
      hasMailAddress: 0,
      agreementVersion: null,
    },
    alertMessage: {
      // body の要約になるような短い語句を書く
      title: '',
      body: '',
    },
    messageDialogs: [{ name: 'ALERT', shown: false }],
    isContentLoaded: false,
    isHistoryBack: false,
    isHome: true,
    isFlyerView: process.env.mode === 'viewer',
    isDomReady: false,
  }),

  getters: {
    categoryIconType: (state) => {
      return state.media.menuType
    },
    categoryIconColor: (state) => {
      const menu = state.media.menu
      if (menu.length === 0) {
        return []
      }
      const color = []
      color.push(menu[0].color1)
      if (menu[0].color2) {
        color.push(menu[0].color2)
      }
      return color
    },
    categoryIconBgColor: (state) => {
      const menu = state.media.menu
      if (menu.length === 0) {
        return {}
      }
      const bgColor = {}
      menu.forEach(item => {
        const colors = []
        colors.push(item.bgColor1)
        if (item.bgColor2) {
          colors.push(item.bgColor2)
        }
        bgColor[item.key] = colors
      })
      return bgColor
    },
    messageDialogShown: (state) => name => {
      const dialog = state.messageDialogs.find(dialog => dialog.name === name)
      return dialog ? dialog.shown : false
    },
    canShowCategory: (state) => category => {
      return state.media?.menu?.findIndex(menu => menu.key === category) > -1
    },
    menuKeyCard: (state) => {
      return state.media?.menu?.some(menuItem => menuItem.key === 'point-card')
    },
    menuChirashi: (state) => {
      return state.media?.menu?.some(menuItem => menuItem.key === 'flyer')
    },
    menuPointBenefits: (state) => {
      if (
        state.media?.menu?.some(menuItem => menuItem.key === 'pricing') ||
        state.media?.menu?.some(menuItem => menuItem.key === 'stamp')
      ) {
        return true
      } else {
        return false
      }
    },
    menuSettings: (state) => {
      // console.log(state.media)
      return state.media?.menu?.some(menuItem => menuItem.key === 'settings')
    },
    canShowExpiredPoint: (state) => {
      return state.chain.dispBalance.expirePoint === 1
    },
  },

  actions: {
    async initializeApp() {
      await this.fetchChainIdByChainKey()
      await this.fetchMedia('tokenless')
    },
    async refreshTokens() {
      if (isRefreshingTokens) {
        return
      }
      const runtimeConfig = useRuntimeConfig()

      isRefreshingTokens = true
      await POST('refresh_token', {
        body: {
          clientKey: runtimeConfig.public.clientKey,
          mediaId: this.mediaId,
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
          impressionKey: this.user.impressionKey,
          reqTime: UNIXTIMESTAMP(),
        },
      })
        .then(res => {
          const accessToken = res.data.accessToken
          const refreshToken = res.data.refreshToken
          this.accessToken = accessToken
          this.refreshToken = refreshToken
          location.reload()
        })
        .catch(() => {
          this.restartApp()
        })
        .finally(() => {
          isRefreshingTokens = false
        })
    },
    logout() {
      this.accessToken = ''
      this.refreshToken = ''
      this.user = {}
      this.isLoggedIn = false
    },
    async fetchChainIdByChainKey() {
      const runtimeConfig = useRuntimeConfig()
      this.mediaId = runtimeConfig.public.mediaId
      this.chainKey = runtimeConfig.public.chainKey

      await POST('get_chain_info', {
        body: {
          clientKey: runtimeConfig.public.clientKey,
          mediaId: this.mediaId,
          chainKey: this.chainKey,
          impressionKey: this.user.impressionKey,
          reqTime: UNIXTIMESTAMP(),
        },
      }).then(res => {
        // API 仕様書には res.data.data.chain とあるが、実装はそうなっていない……
        const chain = res.data
        this.chainId = chain.chainId
      })
    },
    async fetchChain() {
      // if (!state.accessToken) return
      const runtimeConfig = useRuntimeConfig()

      await POST('get_chain_info', {
        body: {
          clientKey: runtimeConfig.public.clientKey,
          mediaId: this.mediaId,
          chainId: this.chainId,
          accessToken: this.accessToken,
          impressionKey: this.user.impressionKey,
          reqTime: UNIXTIMESTAMP(),
        },
      })
        .then(res => {
          // API 仕様書には res.data.data.chain とあるが、実装はそうなっていない……
          const chain = res.data
          this.chain = chain
        })
        .catch(err => {
          const status = err.status
          const error = err.data.error.errorCode
          // アクセストークンが期限切れ
          if (status === 401 && error === '10001') {
            this.refreshToken()
          } else {
            return Promise.reject(err)
          }
        })
    },
    async fetchMedia(paramType = 'tokenful') {
      // TODO: フェーズ2でアクセストークン有りのリクエスト追加
      const runtimeConfig = useRuntimeConfig()

      const data = {
        tokenful: {
          clientKey: runtimeConfig.public.clientKey,
          mediaId: this.mediaId,
          chainId: this.chainId,
          accessToken: this.accessToken,
          impressionKey: this.user.impressionKey,
          reqTime: UNIXTIMESTAMP(),
        },
        tokenless: {
          clientKey: runtimeConfig.public.clientKey,
          mediaId: this.mediaId,
          chainId: this.chainId,
          impressionKey: this.user.impressionKey,
          reqTime: UNIXTIMESTAMP(),
        },
      }
      await POST('get_media_info', {
        body: data[paramType],
      })
        .then(res => {
          const resMedia = res.data.media
          const menuTypes = {
            outline: 'outline',
            round: 'round',
            twotone: 'two-tone',
            sharp: 'sharp',
          }
          const keys = {
            card: 'point-card',
            flyer: 'flyer',
            content: 'content',
            pricing: 'pricing',
            stamp: 'stamp',
            config: 'settings',
          }
          const menuType = menuTypes[resMedia?.menuType]
          const menu = resMedia?.menu?.map(menu => {
            const { key, ...rest } = menu
            return {
              key: keys[key],
              ...rest,
            }
          })
          const media = {
            ...resMedia,
            menuType,
            menu,
          }
          this.media = media
        })
        .catch(err => {
          const status = err.status
          const error = err.data.error.errorCode
          // アクセストークンが期限切れ
          if (status === 401 && error === '10001') {
            this.refreshTokens()
          } else {
            return Promise.reject(err)
          }
        })
    },
    setAlertMessage(message) {
      this.alertMessage = message
    },
    setMessageDialogs({ name, shown }) {
      const isExisting = this.messageDialogs.find(dialog => dialog.name === name)
      if (isExisting) {
        this.messageDialogs = this.messageDialogs.map(dialog => {
          if (dialog.name === name) {
            return { name: dialog.name, shown }
          } else {
            return { name: dialog.name, shown: false }
          }
        })
      } else {
        this.messageDialogs.push({ name, shown })
      }
    },
    confirmImgLoaded() {
      const targetCount = document.querySelectorAll('.extended-image').length
      const targetLoadedCount = document.querySelectorAll(
        '.extended-image.loaded'
      ).length
      if (targetCount === targetLoadedCount) {
        this.isDomReady = true
        return true
      } else {
        return false
      }
    },
    restartApp() {
      this.logout()
      this.accessToken = ''
      this.refreshToken = ''
    },
  }
})